import {ReadAsType} from '../incase-domain/common/fileReaderString';
import {IElementQuestionAnswer, IElementQuestionnaire} from '../incase-domain/elements/base/questionElementBase';

export function getDifference<T>(a: T[], b: T[]): T[] {
    const setB = new Set(b);

    return [...a].filter((x) => !setB.has(x));
}

export function randomString(radix: number): string {
    return (Math.random() + 1).toString(radix).substring(4);
}

export function swapArrayIndices<T>(array: T[], newIndex: number, oldIndex: number): T[] {
    const swappingElement = array[oldIndex];
    const filteredArray = array.filter((element, index) => index !== oldIndex);
    return [...filteredArray.slice(0, newIndex), swappingElement, ...filteredArray.slice(newIndex)];
}

export function swapArrayElement<T>(array: T[], index: number, newElement: T): T[] {
    return [...array.slice(0, index), newElement, ...array.slice(index + 1)];
}

export function removeArrayElement<T>(array: T[], index: number): T[] {
    return array.filter((el, i) => i !== index);
}

export function removeElementFromArray<T>(array: T[], element: T): T[] {
    return array.filter((el) => el !== element);
}

export function getUploadFileReader(files: File[] | null, readAs: ReadAsType): null | {reader: FileReader; file: File}[] {
    if (!files) return null;

    const readersAndFiles: {reader: FileReader; file: File}[] | null = [];

    files.forEach((file) => {
        const reader = new FileReader();
        readAs === 'dataurl' && reader.readAsDataURL(file);
        readAs === 'text' && reader.readAsText(file);
        readersAndFiles.push({reader, file});
    });

    return readersAndFiles;
}

export function getCommentColor<T extends IElementQuestionnaire, K extends IElementQuestionAnswer>(
    question: T,
    answer: K
): string | undefined {
    if (answer.type === 'correct') {
        return question.correctCommentColor;
    }
    if (answer.type === 'incorrect') {
        return question.incorrectCommentColor;
    }
    return question.debatableCommentColor;
}

export function isDeleteButtonDisabled(elementArrayLength: number, minNumberOfItems: number): boolean {
    return elementArrayLength < minNumberOfItems;
}

// Formats bytes into human-readable value
export function formatBytes(bytes: number, decimals?: number, binaryUnits?: boolean): string {
    if (bytes === 0) {
        return '0 Bytes';
    }
    const unitMultiple = binaryUnits ? 1024 : 1000;
    const unitNames =
        unitMultiple === 1024 // 1000 bytes in 1 Kilobyte (KB) or 1024 bytes for the binary version (KiB)
            ? ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
            : ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const unitChanges = Math.floor(Math.log(bytes) / Math.log(unitMultiple));
    return `${parseFloat((bytes / unitMultiple ** unitChanges).toFixed(decimals || 0))} ${unitNames[unitChanges]}`;
}

export function shuffleElementsOrder<T>(array: T[]): T[] {
    const arr = array;
    let currentIndex = array.length || 0;
    let randomIndex;

    // While there remain elements to shuffle.
    while (currentIndex !== 0) {
        // Pick a remaining element.
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;

        // And swap it with the current element.
        [arr[currentIndex], arr[randomIndex]] = [arr[randomIndex], arr[currentIndex]];
    }

    return arr;
}

export function capitalize(input: string): string {
    if (!input) return '';
    return input.charAt(0).toUpperCase() + input.slice(1);
}
